<template>
    <component :is="tag" class="item" :title="itemTitle">
        <template v-if="departure">
            <template v-if="!hideHours">
                {{ format(departure.depart, 'HH:mm') }}
            </template>
            <template v-else>
                <span class="visuallyhidden">{{ format(departure.depart, 'HH') }} {{ t('hour') }}&#32;</span>
                {{ format(departure.depart, 'mm') }}
                <span v-if="selected" class="visuallyhidden">,&#32;{{ t('currently_selected_time') }}</span>
            </template>
            <template v-if="departure.typeEmbarquement === '1'">
                <IconUpArrow width="8" height="14" />
                <span class="visuallyhidden">{{ t('boarding_only') }}</span>
            </template>
            <template v-if="departure.typeEmbarquement === '2'">
                <IconDownArrow width="8" height="14" />
                <span class="visuallyhidden">{{ t('drop_off_only') }}</span>
            </template>
            <span v-if="fridayOnly">
                V
                <span class="visuallyhidden">{{ t('friday_only') }}</span>
            </span>
            <span v-if="isEndOfRoute" class="visuallyhidden">{{ t('route_end') }} {{ departure.lieuFinDesc }}</span>
        </template>
        <div v-else>&mdash;</div>
    </component>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { format } from '@@/js/utils/dateFns';
import IconUpArrow from '@@/icons/up-arrow.svg';
import IconDownArrow from '@@/icons/down-arrow.svg';

const props = defineProps({
    tag: {
        type: String,
        default: 'div',
    },
    departure: {
        type: Object,
        default: () => ({}),
    },
    route: {
        type: Object,
        default: () => ({}),
    },
    dayType: {
        type: String,
        default: 'weekdays',
    },
    hideHours: {
        type: Boolean,
        default: false,
    },
    selected: {
        type: Boolean,
        default: false,
    },
    isEndOfRoute: {
        type: Boolean,
        default: false,
    },
});

const { t } = useI18n({
    messages: {
        fr: {
            hour: 'heure',
            no_departure: 'Arrêt non désservi',
            boarding_only: 'Montée seulement',
            drop_off_only: 'Descente seulement',
            friday_only: 'Vendredi seulement',
            currently_selected_time: 'Horaire présentement sélectionnée',
            route_end: 'Fin de parcours',
        },
        en: {
            hour: 'hour',
            no_departure: 'Unserved stop',
            boarding_only: 'Boarding only',
            drop_off_only: 'Drop off only',
            friday_only: 'Friday only',
            currently_selected_time: 'Currently selected schedule',
            route_end: 'Route end',
        },
    },
});

const fridayOnly = computed(() => {
    if (props.route && props.route.jours && props.dayType === 'weekdays') {
        const weekday = props.route.jours.split('').splice(1, 5);
        return weekday[4] === '1' && weekday.filter((w) => w === '1').length === 1;
    }
    return false;
});

const itemTitle = computed(() => {
    if (!props.departure) return t('no_departure');
    let title = format(props.departure.depart, 'HH:mm');
    if (props.departure.typeEmbarquement === '1') {
        title += ` ${t('boarding_only')}`;
    }
    if (props.departure.typeEmbarquement === '2') {
        title += ` ${t('drop_off_only')}`;
    }
    if (fridayOnly.value) {
        title += ` ${t('friday_only')}`;
    }
    if (props.isEndOfRoute) {
        title += ` ${t('route_end')} ${props.departure.lieuFinDesc}`;
    }
    return title;
});
</script>

<style lang="scss" scoped>
svg {
    vertical-align: baseline;
}
</style>
